import React, { useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { Button } from 'components/ui/button';
import Toggle from 'components/toggle';

import { put } from 'helpers/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import Teams from 'bundles/email/editor/teams/teams';
import { isEqual } from 'lodash';
import { Alert, Form, InputGroup } from 'react-bootstrap-5';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function EmailNotificationsTab({
  notificationCustomizations, notificationEmailConfig, primaryBroker, propertyId, teamsUrl, teamsV2
}) {
  const initEmail = useMemo(() => notificationCustomizations?.reply_to_email || primaryBroker.email || '', []);
  const initFrom = useMemo(() => {
    return notificationCustomizations?.from_alias || primaryBroker.name || '';
  }, []);
  const initNotificationEmailConfig = useMemo(() => notificationEmailConfig || {}, []);

  const [savedReplyToEmail, setSavedReplyToEmail] = useState(initEmail);
  const [savedFromAlias, setSavedFromAlias] = useState(initFrom);
  const [savedNotificationEmailConfig, setSavedNotificationEmailConfig] = useState(initNotificationEmailConfig);

  const [formData, setFormData] = useState({
    replyToEmail: initEmail,
    fromAlias: initFrom,
    notificationEmailConfig: initNotificationEmailConfig
  });

  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const currentReplyToEmail = formData.replyToEmail.trim();
  const currentFromAlias = formData.fromAlias.trim();
  const previousReplyToEmail = savedReplyToEmail.trim();
  const previousFromAlias = savedFromAlias.trim();

  const validEmail = useMemo(() => {
    return EMAIL_REGEX.test(currentReplyToEmail);
  }, [currentReplyToEmail]);

  const dirtyForm = useMemo(() => {
    return (currentReplyToEmail !== previousReplyToEmail) ||
      (currentFromAlias !== previousFromAlias) ||
      !isEqual(formData.notificationEmailConfig, savedNotificationEmailConfig);
  }, [currentFromAlias, currentReplyToEmail, formData, previousFromAlias, previousReplyToEmail]);

  const canSubmit = dirtyForm && validEmail;
  const replyToHasError = dirtyForm && !validEmail;

  const canSetToPrimary = useMemo(() => {
    return (currentReplyToEmail !== primaryBroker.email.trim()) ||
      (currentFromAlias !== primaryBroker.name.trim());
  }, [currentFromAlias, currentReplyToEmail, primaryBroker]);

  const setToPrimaryBroker = () => {
    setFormData({
      ...formData,
      fromAlias: primaryBroker?.name,
      replyToEmail: primaryBroker?.email
    });
  };

  const changesSaved = () => {
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 5000);
  };

  const saveChanges = () => {
    if (!canSubmit) return;

    setSaving(true);
    const replyToEmail = currentReplyToEmail;
    const fromAlias = currentFromAlias;
    const params = {
      emailNotificationCustomizations: {
        reply_to_email: replyToEmail,
        from_alias: fromAlias
      },
      notificationEmailConfig: formData.notificationEmailConfig
    };

    put(`/properties/${propertyId}`, params).then(() => {
      setSavedFromAlias(fromAlias);
      setSavedReplyToEmail(replyToEmail);
      setSavedNotificationEmailConfig(formData.notificationEmailConfig);
      setFormData({
        ...formData,
        replyToEmail,
        fromAlias
      });
      changesSaved();
    });
  };

  const notificationEmailConfigValue = (key) => {
    return (key in formData.notificationEmailConfig) ? formData.notificationEmailConfig[key] : true;
  };

  const updateNotificationEmailConfig = (key, value) => {
    const newNotificationEmailConfig = { ...formData.notificationEmailConfig, [key]: value };
    setFormData({
      ...formData,
      notificationEmailConfig: newNotificationEmailConfig
    });
  };

  const renderAutomaticEmailToggle = (key, label) => {
    return (
      <div className="mb-4 d-flex align-items-center gap-3">
        <Toggle
          checked={notificationEmailConfigValue(key)}
          disabled={saving}
          showText={false}
          theme="ds"
          onChange={e => updateNotificationEmailConfig(key, e.target.checked)}
        />
        <span className="fw-semibold">
          {label}
        </span>
      </div>
    );
  };

  return (
    <div>
      {saved && (
        <Alert className="mb-2 text-center" variant="success">
          Changes Saved
        </Alert>
      )}
      <div className="mb-8">
        <div>
          <div className="mb-2 fw-semibold fs-large">Document Notification Emails</div>
          <div className="mb-4">
            Enable or disable automatic document notification emails sent to your users for this listing.
          </div>
          <div>
            {renderAutomaticEmailToggle('docAccessLevelsChanged', 'New documents available')}
            {renderAutomaticEmailToggle('vaultAccessApproved', 'Waitlist access is approved')}
          </div>
        </div>
      </div>
      <div className="mb-4">
        Set the sender name and reply-to address for these automated emails.
      </div>
      <Form
        autoComplete="off"
        className="form-vertical-field-container mb-8"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          saveChanges();
        }}
      >
        <Form.Group className="form-group">
          <Form.Label className="form-label">From Alias</Form.Label>
          <InputGroup className="w-100 form-input-container">
            <Form.Control
              className="form-control"
              disabled={saving}
              type="text"
              value={formData.fromAlias}
              onChange={e => setFormData({ ...formData, fromAlias: e.target.value })}
            />
            {currentFromAlias && currentFromAlias === previousFromAlias && (
              <InputGroup.Text>
                <FontAwesomeIcon className="text-success" icon={faCheck}/>
              </InputGroup.Text>
            )}
          </InputGroup>
          <div className="text-muted">
            Your emails will be delivered using this name
          </div>
        </Form.Group>
        <Form.Group className={`form-group required ${replyToHasError ? 'has-error' : ''}`}>
          <Form.Label className="form-label">Reply-To Email</Form.Label>
          <InputGroup className="w-100 form-input-container" hasValidation>
            <Form.Control
              className={replyToHasError ? 'form-control is-invalid' : 'form-control'}
              disabled={saving}
              required
              type="email"
              value={formData.replyToEmail}
              onChange={e => setFormData({ ...formData, replyToEmail: e.target.value })}
            />
            {currentReplyToEmail && currentReplyToEmail === previousReplyToEmail && (
              <InputGroup.Text>
                <FontAwesomeIcon className="text-success" icon={faCheck}/>
              </InputGroup.Text>
            )}
          </InputGroup>
          {replyToHasError && (
            <div className="invalid-feedback d-block">Enter a valid email address</div>
          )}
          <div className="text-muted">
            Replies to your emails will go to this address
          </div>
        </Form.Group>
        <div className="text-end">
          <Button
            className="me-2"
            disabled={!canSetToPrimary}
            label="Use Primary Broker"
            type="button"
            variant="secondary"
            onClick={setToPrimaryBroker}
          />
          <Button disabled={!canSubmit} label="Save Changes" type="submit"/>
        </div>
      </Form>

      {teamsV2 && <Teams searchUrl="/email_teams" teamsUrl={teamsUrl} teamsV2={teamsV2}/>}
    </div>
  );
}

EmailNotificationsTab.propTypes = {
  notificationCustomizations: PropTypes.shape({
    reply_to_email: PropTypes.string,
    from_alias: PropTypes.string
  }),
  notificationEmailConfig: PropTypes.shape({
    docAccessLevelsChanged: PropTypes.bool,
    vaultAccessApproved: PropTypes.bool
  }),
  primaryBroker: PropTypes.shape({
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired
  }),
  propertyId: PropTypes.number.isRequired,
  teamsUrl: PropTypes.string.isRequired,
  teamsV2: PropTypes.bool
};

export default EmailNotificationsTab;
